/**
 * Profile API
 * 
 * Handles user profile management and updates.
 */

import { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL } from '../api';

export interface ProfileUpdateRequest {
  // New name fields (v0.8.0)
  first_name?: string;
  last_name?: string;
  public_name_mode?: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
  // Legacy field for compatibility
  name?: string;
  // Clearable/nullable fields: `null` clears the column, `undefined` omits the
  // field. Never send `undefined` to CLEAR — JSON.stringify drops it (#2931).
  tagline?: string | null;
  birthday_day?: number | null;
  birthday_month?: number | null;
  location?: string | null;
  primary_sport?: string | null;
  secondary_sports?: string[] | null;
  distance_unit?: 'mi' | 'km'; // Issue #1083: User's preferred distance unit
  timezone?: string; // #1647: IANA tz for virtual-event notifications
}

// Mirrors the backend `UserResponse` envelope emitted by
// `formatUserForResponse` (backend/src/routes/profile.ts:29 → returns
// UserResponse, backend/src/types.ts:438). Fields the handler does NOT send
// (e.g. `name`, `subscription_level`) must not appear here — reading them off
// the profile response only ever yielded `undefined` (Rule 55: pin the handler
// envelope, not a shared TS type that can drift).
export interface User {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  public_name_mode: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
  public_display_name: string;
  tagline: string | null;
  birthday_day: number | null;
  birthday_month: number | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
  profile_image_url: string | null;
  cover_image_url: string | null; // #1234: Custom profile header photo
  // Phone (#1461 - SMS notifications)
  phone: string | null;
  phone_verified: boolean;
  // user_level removed - see #785
  distance_unit: 'mi' | 'km'; // Issue #1083: User's preferred distance unit
  timezone: string | null; // #1647: IANA tz for virtual-event notifications
  // D1 unix seconds (Rule 9). Backend UserResponse declares these `number`
  // (backend/src/types.ts:481-482); formatUserForResponse passes them through
  // from the DB row unchanged (backend/src/routes/profile.ts:71-72).
  created_at: number;
  updated_at: number;
}

export interface ProfileResponse {
  profile: User;
}

export const profileApi = {
  /**
   * Get current user's profile
   * GET /profile
   */
  getProfile: () => apiGet<ProfileResponse>('/profile'),

  /**
   * Update current user's profile
   * PUT /profile
   */
  updateProfile: (data: ProfileUpdateRequest) => 
    apiPut<ProfileResponse>('/profile', data),

  /**
   * Remove profile image
   * DELETE /profile/image
   */
  removeProfileImage: () =>
    apiDelete<{ ok: true }>('/profile/image'),

  /**
   * Upload profile cover image (#1234)
   * POST /upload/profile-cover
   */
  uploadCoverImage: async (file: File): Promise<{ image_url: string }> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/upload/profile-cover`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Failed to upload cover image');
    }

    return response.json();
  },

  /**
   * Request phone verification code (#1461)
   * POST /profile/phone/request
   */
  requestPhoneVerification: (phone: string) =>
    apiPost<{ success: boolean; message: string }>('/profile/phone/request', { phone }),

  /**
   * Verify phone with code (#1461)
   * POST /profile/phone/verify
   */
  verifyPhone: (code: string) =>
    apiPost<ProfileResponse>('/profile/phone/verify', { code }),

  /**
   * Remove phone number (#1461)
   * DELETE /profile/phone
   */
  removePhone: () =>
    apiDelete<ProfileResponse>('/profile/phone'),

  /**
   * Request an add-email verification code (#2930)
   * POST /profile/email/request
   *
   * Recovery prerequisite for phone-only STOP-locked users: a synthetic
   * `{phone}@sms.oro.fit` account adds its FIRST real email so the magic-link
   * sign-in path becomes usable. Backend gates this to synthetic accounts only
   * (a real-email account gets a 400 → use the #1335 change flow instead) and
   * sends a 6-digit code to the NEW address (not a magic link, mirroring phone).
   */
  requestEmailVerification: (email: string) =>
    apiPost<{ success: boolean; message: string }>('/profile/email/request', { email }),

  /**
   * Verify the add-email code and commit the new email (#2930)
   * POST /profile/email/verify
   *
   * Returns the updated profile (its `email` is now the real address, so the
   * account is no longer synthetic). Deliberately does NOT invalidate the
   * session (owner decision on #2930): the live session is the locked-out user's
   * only recovery window.
   */
  verifyEmail: (code: string) =>
    apiPost<ProfileResponse>('/profile/email/verify', { code }),

  /**
   * Remove profile cover image (#1234)
   * DELETE /upload/profile-cover
   */
  removeCoverImage: async () => {
    const response = await fetch(`${API_BASE_URL}/upload/profile-cover`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to remove cover image');
    }

    return response.json();
  },
};
